import { useState } from "react";

import type { GeoReport } from "../../../api/geoService";
import { AudienceTable, CoreSummary, MentionCharts } from "./OverviewSections";
import { PlatformDetailTabs, PlatformGrid8 } from "./PlatformSections";
import { ReportHero } from "./ReportHero";
import {
  BaselineCompareSec,
  CompetitorDual,
  ContentPlanStages,
  DiagnosisSec,
  SourceInvestTable,
} from "./StrategySections";
import { SearchOverviewSec, TrustAssetsSec } from "./TrustSearchSections";

/**
 * 各板块「是否会渲染」谓词(codex S1 P2:板块缺席时锚点点了没反应 → 目录按在场过滤)。
 * 与各组件自身的 null-guard 同口径;两源一致性由 GeoReportV2Panel.test 的
 * 「谓词 ↔ 实际 DOM」测试锁死,改任何一边不同步都会红。
 */
export function v2SectionAvailability(report: GeoReport): Record<string, boolean> {
  const platforms = report.platform_details?.platforms ?? [];
  return {
    "geo-sec-audience": Boolean(
      report.audience_stats?.available && report.audience_stats.segments?.length
    ),
    "geo-sec-summary": Boolean(report.score_breakdown?.components?.length),
    "geo-sec-charts": platforms.length >= 3,
    "geo-sec-trust": Boolean(report.trust_assets?.available && report.trust_assets.assets?.length),
    "geo-sec-search": Boolean(report.search_overview?.available),
    "geo-sec-grid": platforms.length > 0,
    "geo-sec-platforms": platforms.length > 0,
    "geo-sec-comp": true, // 竞品段恒渲染(空态文案在卡内)
    "geo-sec-diagnosis": platforms.length > 0 || Boolean(report.shortboards?.length),
    "geo-sec-plan": Boolean(report.content_plan?.available && report.content_plan.months?.length),
    "geo-sec-invest": Boolean(
      report.source_investment?.available && report.source_investment.rows?.length
    ),
    "geo-sec-baseline": Boolean(report.baseline_compare?.available),
  };
}

/**
 * 12 板块锚点(报告目录;与各 section id 对齐)。
 *
 * 0907 重排(照设计稿):引用来源 → 竞品可见 → 诊断短板 → 平台详情(原话回放) → 模型提及
 * 排到最前(买家最想先看「AI 从哪抄的答案 / 打得过打不过 / 差在哪 / 原话长什么样」),
 * 其余板块维持原相对顺序不变。12 板块只重排、不删减。
 */
export const V2_ANCHORS: Array<[string, string, string]> = [
  ["01", "引用来源", "geo-sec-search"],
  ["02", "竞品可见", "geo-sec-comp"],
  ["03", "诊断短板", "geo-sec-diagnosis"],
  ["04", "平台详情", "geo-sec-platforms"],
  ["05", "模型提及", "geo-sec-grid"],
  ["06", "用户群体", "geo-sec-audience"],
  ["07", "核心摘要", "geo-sec-summary"],
  ["08", "提及图谱", "geo-sec-charts"],
  ["09", "信任资产", "geo-sec-trust"],
  ["10", "内容计划", "geo-sec-plan"],
  ["11", "信源投放", "geo-sec-invest"],
  ["12", "基线对比", "geo-sec-baseline"],
];

interface GeoReportV2PanelProps {
  report: GeoReport;
  createdAt?: string;
  /** 挂在报告根节点上的 id,给「导出 HTML」定位要 clone 的容器用。 */
  rootId?: string;
  isBaseline?: boolean;
  onExportHtml?: () => void;
  onExportPdf?: () => void;
  onRepointBaseline?: () => void;
  repointing?: boolean;
}

/**
 * S1 报告 v2:照实习生版式 12 板块全量重建(只加不减)。
 * 旧 8 锚点内容全部收编:五维→六构成 / 检索归因·引用源→01 / 原话墙→04 /
 * 阵地体检→09 / 负面·画像→02 / 策略卡→03。
 */
export function GeoReportV2Panel({
  report,
  createdAt,
  rootId,
  isBaseline,
  onExportHtml,
  onExportPdf,
  onRepointBaseline,
  repointing,
}: GeoReportV2PanelProps) {
  // 05 模型提及概览的卡 ↔ 04 平台详情的 tab 共用一个选中态:点 05 的卡 = 切 04 的 tab 并滚过去
  // (测试反馈:「点击对应模型可以跳转到对应的平台详情」)。state 必须提到这里 —— 放在
  // PlatformDetailTabs 内部时,05 那边点了没有任何东西能改它。
  const [activePlatform, setActivePlatform] = useState(0);
  return (
    <div id={rootId} className="space-y-4">
      <ReportHero
        report={report}
        createdAt={createdAt}
        isBaseline={isBaseline}
        onExportHtml={onExportHtml}
        onExportPdf={onExportPdf}
        onRepointBaseline={onRepointBaseline}
        repointing={repointing}
      />
      <SearchOverviewSec report={report} />
      <CompetitorDual report={report} />
      <DiagnosisSec report={report} />
      <PlatformDetailTabs report={report} active={activePlatform} onActive={setActivePlatform} />
      <PlatformGrid8 report={report} onPick={setActivePlatform} />
      <AudienceTable report={report} />
      <CoreSummary report={report} />
      <MentionCharts report={report} />
      <TrustAssetsSec report={report} />
      <ContentPlanStages report={report} />
      <SourceInvestTable report={report} />
      <BaselineCompareSec report={report} />
    </div>
  );
}
